import React, { useEffect, useCallback } from 'react';
import { gameLogger, LogLevel, LogEntry } from '../utils/GameLogger';

// Hooks
import { useLogViewerState } from './LogViewer/hooks/useLogViewerState';

// Components
import { LogViewerToolbar } from './LogViewer/components/LogViewerToolbar';
import { LogEntryRow } from './LogViewer/components/LogEntryRow';

/**
 * LogViewerContent — propless body of the Debug Console (Phase 2 fold).
 *
 * WindowHost mounts this inside the shared ToolWindow chrome (only while the
 * `logViewer` window is open — opened via windowBridge.toggle('logViewer')), so
 * it no longer owns its own GlassPanel chrome / LogViewerHeader / useDraggable /
 * resize handle. The header's title + minimize + close are now ToolWindow's; its
 * stats + PAUSED indicator move to the compact status row below. F8 still toggles
 * auto-refresh; F2 (toggle) is handled by the engine. Player-free (gameLogger
 * singleton).
 */
export const LogViewerContent: React.FC = () => {
  // The host mounts this body only while the window is open, so the state hook
  // runs exactly while the console is visible.
  const { logs, filter, setFilter, stats, autoRefresh, setAutoRefresh, refreshLogs } = useLogViewerState(true);

  const handleDownload = (format: 'text' | 'json') => {
    gameLogger.downloadLog(format);
  };

  const handleClearLogs = () => {
    if (confirm('Are you sure?')) {
      gameLogger.clearLogs();
      refreshLogs();
    }
  };

  const handleToggleLogging = () => {
    gameLogger.isLoggingActive() ? gameLogger.stopLogging() : gameLogger.startLogging();
    refreshLogs();
  };

  const handleCopy = (mode: 'all' | 'filtered' | 'warn-error' | 'error') => {
    let logsToCopy: LogEntry[] = [];
    if (mode === 'all') logsToCopy = gameLogger.getLogs();
    else if (mode === 'filtered') logsToCopy = logs;
    else if (mode === 'error') logsToCopy = gameLogger.getLogs().filter(log => log.level >= LogLevel.ERROR);

    const text = logsToCopy.map(log => `[${log.timestamp}] [${LogLevel[log.level]}] [${log.category}] ${log.message}`).join('\n\n');
    navigator.clipboard.writeText(text);
  };

  // F8 toggles auto-refresh (kept). F2 (open/close) is the engine's job now.
  const handleHotkey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'F8') { e.preventDefault(); setAutoRefresh(!autoRefresh); }
  }, [autoRefresh, setAutoRefresh]);

  useEffect(() => {
    document.addEventListener('keydown', handleHotkey);
    return () => document.removeEventListener('keydown', handleHotkey);
  }, [handleHotkey]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Compact status row — replaces the old draggable LogViewerHeader (title +
          minimize + close are ToolWindow's now). */}
      <div style={{
        padding: '6px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 11,
        color: '#94a3b8',
        background: 'rgba(0,0,0,0.2)',
      }}>
        <span>📊 {stats ? `${stats.total} logs` : '—'}{!autoRefresh && <span style={{ color: '#e74c3c', marginLeft: 6 }}>(PAUSED)</span>}</span>
      </div>

      <LogViewerToolbar
        filter={filter}
        setFilter={setFilter}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        refreshLogs={refreshLogs}
        handleDownload={handleDownload}
        handleCopy={handleCopy}
        handleClearLogs={handleClearLogs}
        handleToggleLogging={handleToggleLogging}
      />

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 0, backgroundColor: 'rgba(0,0,0,0.2)' }}>
        {logs.length === 0 ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)' }}>No logs match your filters</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {logs.map((log, index) => <LogEntryRow key={index} log={log} index={index} />)}
          </div>
        )}
      </div>

      <div style={{ padding: '4px 12px', background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 10, color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
        <span>Session: {gameLogger.getSessionId()?.substring(0, 8)}...</span>
        <span>Memory: {logs.length} / 150 items</span>
      </div>
    </div>
  );
};
